// Obróbka zdjęć stanu sprzętu W PRZEGLĄDARCE (canvas, zero zależności backendu).
//
// Jeden mechanizm, dwa zastosowania:
//  - `prepareUploadPhoto` pomniejsza zdjęcie PRZED wysłaniem (od 22.09.2026),
//  - `makeThumbnail` robi lekką miniaturę do listy dokumentacji (P5.5).
//
// Do 22.09.2026 oryginał szedł na serwer w pełnym rozmiarze („materiał dowodowy”). Zmiana ma
// konkretny powód: zdjęcie z telefonu to ok. 3 MB (limit serwera 8 MB), a każde trafia też do
// każdego nocnego archiwum kopii zapasowych. Jedna wypożyczalnia fotografująca każde wydanie
// potrafiła w ten sposób zapełnić dysk produkcji w kilka tygodni sezonu, a pełny dysk zatrzymuje
// bazę u wszystkich firm naraz. Dowodowość zostaje: dłuższy bok 2048 px przy jakości 0,82
// pokazuje rysę czy pęknięcie, jeśli zdjęcie jest na nie skadrowane, a plik jest kilka razy mniejszy.
//
// Świadome skutki:
//  - Przekodowanie przez canvas zdejmuje EXIF, w tym GPS i datę zrobienia zdjęcia. NIE jest to
//    jednak jedyna linia: zdjęcie do 1 MB i nieudane przekodowanie idą w oryginale, więc od
//    2026-09-24 (FAZA T, T.4) metadane wycina SERWER z każdego pliku przed zapisem
//    (`Login/backend/lib/stripImageMetadata.js`), zostawiając tylko orientację.
//  - Limit 8 MB na serwerze ZOSTAJE jako bezpiecznik: pomniejszanie da się obejść wysyłką
//    wprost do API, a stara wersja panelu z pamięci przeglądarki jeszcze przez chwilę wyśle
//    oryginał.
//  - Każdy błąd oznacza wysłanie ORYGINAŁU, nigdy utratę zdjęcia.

export const ZDJECIE_MAX_BOK = 2048;
export const ZDJECIE_JAKOSC = 0.82;
// Zdjęcie lżejsze niż to idzie bez zmian: niewiele zyskamy, a ponowne kodowanie JPEG-a tylko
// pogarsza jakość dowodu.
export const ZDJECIE_BEZ_ZMIAN_DO_BAJTOW = 1024 * 1024;
// Formaty, które przyjmuje serwer (Login/backend/lib/stripImageMetadata.js). Zmiana tam wymaga zmiany tu.
export const FORMATY_SERWERA = ['image/jpeg', 'image/png', 'image/webp'];

/** Wymiary po pomniejszeniu tak, by dłuższy bok nie przekraczał `maxBok`. Nigdy nie powiększa. */
export function docelowyRozmiar(szer, wys, maxBok) {
  const skala = Math.min(1, maxBok / Math.max(szer, wys));
  return { w: Math.max(1, Math.round(szer * skala)), h: Math.max(1, Math.round(wys * skala)) };
}

// Obraz wczytujemy przez <img>, a nie `createImageBitmap`: przy rysowaniu elementu <img>
// przeglądarka stosuje orientację zapisaną w EXIF (CSS `image-orientation: from-image`, domyślne
// od 2020 roku), a `createImageBitmap` robił to różnie w starszych przeglądarkach. Obrócone o 90°
// zdjęcie dowodu to realny problem przy sporze o kaucję.
async function przekoduj(file, maxBok, jakosc) {
  const url = URL.createObjectURL(file);
  let canvas = null;
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const { w, h } = docelowyRozmiar(img.naturalWidth, img.naturalHeight, maxBok);
    canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // JPEG nie ma przezroczystości: bez białego tła przezroczyste miejsca PNG wychodzą czarne.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', jakosc));
  } finally {
    URL.revokeObjectURL(url);
    // Safari na iOS ma wspólny limit pamięci płócien i zwalnia je dopiero przy sprzątaniu
    // pamięci. Bez wyzerowania rozmiaru po kilkudziesięciu zdjęciach w jednej sesji pomniejszanie
    // po cichu przestawało działać i leciały oryginały (przegląd PR #184).
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}

/**
 * Zdjęcie gotowe do wysłania: pomniejszone JPEG albo oryginał, gdy pomniejszanie nic nie daje
 * lub się nie udało. Zwraca `File`, więc wywołujący dokłada go do FormData jak dotąd.
 * `przekodujFn` podmieniają testy (jsdom nie ma canvasa).
 */
export async function prepareUploadPhoto(file, {
  maxBok = ZDJECIE_MAX_BOK,
  jakosc = ZDJECIE_JAKOSC,
  bezZmianDo = ZDJECIE_BEZ_ZMIAN_DO_BAJTOW,
  przekodujFn = przekoduj,
} = {}) {
  if (!file || !/^image\//.test(file.type || '')) return file;
  // Format, którego serwer nie przyjmie (np. HEIC z iPhone'a, gdy w oknie wyboru zdjęcia ustawiono
  // „Format: Bieżący”), przekodowujemy ZAWSZE: mały HEIC szedł dawniej bez zmian i serwer odpowiadał
  // 400 (sprawdzone w symulatorze iOS 26.5, 27.09.2026). Safari dekoduje HEIC w <img>, więc canvas
  // oddaje z niego JPEG.
  const przyjmowany = FORMATY_SERWERA.includes(file.type);
  if (przyjmowany && file.size <= bezZmianDo) return file;
  try {
    const blob = await przekodujFn(file, maxBok, jakosc);
    if (!blob || !blob.size) return file;
    // Mniejszy plik liczy się tylko przy formacie, który serwer i tak przyjmie. JPEG z HEIC-a bywa
    // WIĘKSZY od oryginału (25 KB -> 87 KB), a oryginał nie przejdzie, więc JPEG wygrywa zawsze.
    if (przyjmowany && blob.size >= file.size) return file;
    const nazwa = `${(file.name || 'zdjecie').replace(/\.[^.]+$/, '')}.jpg`;
    return new File([blob], nazwa, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (_) {
    return file;
  }
}

// P5.5: miniatura do listy dokumentacji, żeby lista nie ciągnęła pełnych plików (szybciej na
// telefonie). Błąd albo brak zysku = null, a backend pokaże wtedy zdjęcie główne.
export async function makeThumbnail(file, maxSize = 400, quality = 0.7, { przekodujFn = przekoduj } = {}) {
  if (!file || !/^image\//.test(file.type || '')) return null;
  try {
    const blob = await przekodujFn(file, maxSize, quality);
    if (blob && blob.size > 0 && blob.size < file.size) return blob;
    return null;
  } catch (_) {
    return null;
  }
}
